"use client";

import * as React from "react";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type TechRow = {
  pms: string;
  integration: string;
  reliability: string;
  speed: string;
  notes: string;
  recommended: boolean;
};
const categories = [{
  id: "cloud",
  label: "Cloud PMS"
}, {
  id: "legacy",
  label: "Legacy Systems"
}, {
  id: "channel",
  label: "Channel Managers"
}, {
  id: "booking",
  label: "Booking Engines"
}];
const cloudRows: TechRow[] = [{
  pms: "Mews",
  integration: "API-First",
  reliability: "99.9%",
  speed: "Fast",
  notes: "Seamless data flow",
  recommended: true
}, {
  pms: "Cloudbeds",
  integration: "Robust",
  reliability: "99.8%",
  speed: "Fast",
  notes: "Highly scalable",
  recommended: false
}, {
  pms: "Stayntouch",
  integration: "Mobile",
  reliability: "99.9%",
  speed: "Instant",
  notes: "Modern architecture",
  recommended: false
}, {
  pms: "Oracle",
  integration: "Enterprise",
  reliability: "99.9%",
  speed: "Stable",
  notes: "Industry standard",
  recommended: false
}, {
  pms: "RoomRaccoon",
  integration: "Direct",
  reliability: "99.7%",
  speed: "Fast",
  notes: "User-friendly",
  recommended: false
}];
const legacyRows: TechRow[] = [{
  pms: "Opera V5",
  integration: "Complex",
  reliability: "99.5%",
  speed: "Moderate",
  notes: "Requires middleware",
  recommended: false
}, {
  pms: "Infor",
  integration: "Custom",
  reliability: "99.6%",
  speed: "Moderate",
  notes: "Legacy support",
  recommended: false
}, {
  pms: "Visual",
  integration: "Limited",
  reliability: "99.0%",
  speed: "Slow",
  notes: "Needs upgrade",
  recommended: true
}, {
  pms: "Autoclerk",
  integration: "Standard",
  reliability: "99.2%",
  speed: "Moderate",
  notes: "Stable core",
  recommended: false
}, {
  pms: "HotelKey",
  integration: "Direct",
  reliability: "99.4%",
  speed: "Fast",
  notes: "Good integration",
  recommended: false
}];
const channelRows: TechRow[] = [{
  pms: "SiteMinder",
  integration: "Global",
  reliability: "99.9%",
  speed: "Instant",
  notes: "Market leader",
  recommended: true
}, {
  pms: "RateGain",
  integration: "Advanced",
  reliability: "99.9%",
  speed: "Fast",
  notes: "Data-heavy",
  recommended: false
}, {
  pms: "D-Edge",
  integration: "Direct",
  reliability: "99.8%",
  speed: "Fast",
  notes: "European focus",
  recommended: false
}, {
  pms: "Staah",
  integration: "Simple",
  reliability: "99.7%",
  speed: "Fast",
  notes: "Easy setup",
  recommended: false
}, {
  pms: "Parity",
  integration: "Niche",
  reliability: "99.5%",
  speed: "Moderate",
  notes: "Parity focused",
  recommended: false
}];
const bookingRows: TechRow[] = [{
  pms: "BookingSuite",
  integration: "Native",
  reliability: "99.9%",
  speed: "Fast",
  notes: "High conversion",
  recommended: false
}, {
  pms: "TravelClick",
  integration: "Robust",
  reliability: "99.8%",
  speed: "Fast",
  notes: "Enterprise grade",
  recommended: false
}, {
  pms: "Direct",
  integration: "Custom",
  reliability: "99.9%",
  speed: "Instant",
  notes: "Best for direct",
  recommended: true
}, {
  pms: "SynXis",
  integration: "Global",
  reliability: "99.9%",
  speed: "Stable",
  notes: "CRS integration",
  recommended: false
}, {
  pms: "Vertical",
  integration: "Direct",
  reliability: "99.6%",
  speed: "Fast",
  notes: "Simple flow",
  recommended: false
}];
const techData: Record<string, TechRow[]> = {
  cloud: cloudRows,
  legacy: legacyRows,
  channel: channelRows,
  booking: bookingRows
};
function TechTable({
  rows
}: {
  rows: TechRow[];
}) {
  return <TooltipProvider delayDuration={150}>
      <Table className="border-collapse">
        <TableHeader>
          <TableRow className="border-b border-slate-200 hover:bg-transparent">
            <TableHead className="font-default text-xs uppercase tracking-[0.18em] text-slate-500 sticky left-0 bg-slate-50">
              System
            </TableHead>
            <TableHead className="font-default text-xs uppercase tracking-[0.18em] text-slate-500">
              Integration
            </TableHead>
            <TableHead className="font-default text-xs uppercase tracking-[0.18em] text-slate-500">
              Uptime
            </TableHead>
            <TableHead className="font-default text-xs uppercase tracking-[0.18em] text-slate-500">
              Speed
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, i) => <Tooltip key={i}>
                <TableRow data-index={i} className="border-b border-dashed border-slate-200 odd:bg-slate-100/50 transition-colors duration-200 hover:bg-blue-50 cursor-default">
                  <TooltipTrigger asChild>
                    <TableCell className={row.recommended ? "sticky left-0 bg-blue-600 text-white font-default font-semibold text-base" : "sticky left-0 bg-slate-50 font-default text-base text-slate-900"}>
                      {row.pms}
                    </TableCell>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="bg-blue-900 text-white font-default text-[0.7rem] uppercase tracking-[0.12em]">
                    {row.notes}
                  </TooltipContent>
                  <TableCell className="font-default text-base text-slate-700">
                    {row.integration}
                  </TableCell>
                  <TableCell className="font-default text-base text-slate-700">
                    {row.reliability}
                  </TableCell>
                  <TableCell className="font-default text-base text-slate-700">
                    {row.speed}
                  </TableCell>
                </TableRow>
            </Tooltip>)}
        </TableBody>
      </Table>
    </TooltipProvider>;
}
export default function PmsIntegration() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="1935" id="pmsintegrationdeepdive" className="bg-slate-50 text-slate-900 py-24 md:py-36 px-5 md:px-10">
      <div className="mx-auto max-w-[1320px]">
        <div ref={ref} className={"relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-end transition-all duration-700 " + (isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6")}>
          <span aria-hidden="true" className="pointer-events-none absolute -top-10 left-0 select-none font-default font-bold text-slate-900/[0.06] leading-none text-[8rem] md:text-[12rem]">
            03
          </span>

          <div className="lg:col-span-8 relative z-10">
            <p className="font-default text-xs md:text-sm uppercase tracking-[0.22em] text-blue-600 mb-6">
              § 03 / TECH STACK INTEGRATION
            </p>
            <h2 className="font-default font-semibold leading-[0.98] tracking-[-0.02em] text-[clamp(2rem,4.5vw,3.75rem)] text-slate-900">
              PMS &middot; Channel &middot; Booking,{" "}
              <span className="relative inline-block">
                Unified
                <span className="absolute -bottom-1 left-0 h-[3px] w-full bg-blue-600" />
                <span className="absolute -bottom-1 right-[-6px] h-[7px] w-[7px] rounded-lg bg-blue-600" />
              </span>{" "}
              Performance
            </h2>
            <p className="mt-6 max-w-xl font-default text-base md:text-lg leading-relaxed text-slate-700">
              Optimize your hotel's tech ecosystem. We evaluate PMS connectivity, 
              channel manager efficiency, and booking engine conversion rates to 
              ensure your data flows seamlessly.
            </p>
          </div>

          <div className="lg:col-span-4 relative z-10">
            <Tabs defaultValue="cloud" className="w-full">
              <TabsList className="flex w-full flex-wrap h-auto gap-1 bg-slate-200 p-1 rounded-lg">
                {categories.map((cat, i) => <TabsTrigger key={cat.id} data-index={i} value={cat.id} className="flex-1 font-default text-xs uppercase tracking-[0.1em] data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-md">
                    {cat.label}
                  </TabsTrigger>)}
              </TabsList>

              {categories.map((cat, i) => <TabsContent key={cat.id} data-index={i} value={cat.id} className="mt-10 animate-in fade-in-0 duration-300">
                  <div className="block md:hidden">
                    <div className="rounded-lg border border-slate-200 p-1">
                      <ScrollArea className="w-full whitespace-nowrap">
                        <div className="min-w-[480px] p-3">
                          <TechTable rows={techData[cat.id]} />
                        </div>
                        <ScrollBar orientation="horizontal" />
                      </ScrollArea>
                    </div>
                  </div>
                </TabsContent>)}
            </Tabs>
          </div>
        </div>

        <div className="my-10 hidden md:block border-t border-slate-200" />

        <div className="hidden md:block">
          <Tabs defaultValue="cloud" className="w-full">
            <TabsList className="mb-8 inline-flex h-auto gap-1 bg-slate-200 p-1 rounded-lg">
              {categories.map((cat, i) => <TabsTrigger key={cat.id} data-index={i} value={cat.id} className="font-default text-xs uppercase tracking-[0.12em] px-5 data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-md">
                  {cat.label}
                </TabsTrigger>)}
            </TabsList>

            {categories.map((cat, i) => <TabsContent key={cat.id} data-index={i} value={cat.id} className="animate-in fade-in-0 duration-300">
                <div className="rounded-lg border border-slate-200 p-4 md:p-6 bg-white shadow-md">
                  <TechTable rows={techData[cat.id]} />
                </div>
                <p className="mt-4 font-default text-[0.7rem] uppercase tracking-[0.12em] text-slate-500">
                  Blue highlighted rows indicate our recommended solutions for 
                  maximum revenue impact.
                </p>
              </TabsContent>)}
          </Tabs>
        </div>
      </div>
    </section>;
}